import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
  PhoneIcon,
  ChatBubbleLeftIcon,
  EnvelopeIcon,
  CalendarDaysIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import callService from "../../services/callService";
import smsService from "../../services/smsService";
import smsTemplateService from "../../services/smsTemplateService";
import recareService from "../../services/recareService";
import api from "../../services/api";
import toast from "react-hot-toast";

const CONTACT_OUTCOMES = [
  { value: "connected", label: "Connected - Spoke with patient", group: "contact" },
  { value: "appointment_booked", label: "Appointment Booked", group: "contact" },
  { value: "callback_scheduled", label: "Callback Scheduled", group: "contact" },
  { value: "not_interested", label: "Not Interested", group: "contact" },
  { value: "follow_up", label: "Needs Follow Up", group: "contact" },
  { value: "voicemail", label: "Voicemail", group: "no_contact" },
  { value: "no_answer", label: "No Answer", group: "no_contact" },
  { value: "busy", label: "Busy", group: "no_contact" },
  { value: "wrong_number", label: "Wrong Number", group: "no_contact" },
  { value: "other", label: "Other", group: "no_contact" },
];

const OUTCOME_LABEL_MAP = Object.fromEntries(CONTACT_OUTCOMES.map((o) => [o.value, o.label]));

function buildAutoNote(outcomeValue, followUpActions) {
  const parts = [];
  if (outcomeValue && OUTCOME_LABEL_MAP[outcomeValue]) {
    parts.push(`Outcome: ${OUTCOME_LABEL_MAP[outcomeValue]}`);
  }
  if (followUpActions.length > 0) {
    parts.push(`Actions: ${followUpActions.join(", ")}`);
  }
  return parts.join("\n");
}

const SELECT_STYLE_FILLED = { color: "#1e293b" };
const SELECT_STYLE_PLACEHOLDER = { color: "#94a3b8" };
const OPTION_STYLE = { color: "#1e293b" };
const OPTION_PLACEHOLDER_STYLE = { color: "#94a3b8" };

const STORAGE_KEY = "chairside_pending_wrapup";
const WRAPUP_CACHE_TTL_MS = 5 * 60 * 1000;
const stageCache = new Map();
const communicationCache = new Map();

function mapStatusToOutcome(status) {
  const s = String(status || "").toLowerCase();
  if (s === "busy") return "busy";
  if (s === "no-answer") return "no_answer";
  if (s === "failed") return "other";
  if (s === "voicemail") return "voicemail";
  if (s === "completed") return "connected";
  return "";
}

const WrapUpModal = ({
  isOpen,
  onClose,
  onComplete,
  onCallAgain,
  lead,
  twilioCallSid,
  lastCallStatus,
  stages: propStages = [],
  currentStageId,
  pipelineId: propPipelineId,
  organizationId: propOrgId,
  locationId,
  sessionId,
  isPowerDialer = false,
  initialOutcome,
  prefetchedSmsFolders = null,
  prefetchedEmailTemplates = null,
}) => {
  const { user } = useAuth();
  const agentFullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Your Agent";

  const [outcome, setOutcome] = useState("");
  const [customNotes, setCustomNotes] = useState(null);
  const [selectedStageId, setSelectedStageId] = useState("");
  const [callbackDate, setCallbackDate] = useState("");
  const [saving, setSaving] = useState(false);

  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [bookingProviderId, setBookingProviderId] = useState("");
  const [bookingProviderName, setBookingProviderName] = useState("");
  const [providers, setProviders] = useState([]);

  const [smsFolders, setSmsFolders] = useState(prefetchedSmsFolders || []);
  const [selectedSmsFolderId, setSelectedSmsFolderId] = useState("");
  const [selectedSmsTemplateId, setSelectedSmsTemplateId] = useState("");
  const [smsBody, setSmsBody] = useState("");

  const [emailTemplates, setEmailTemplates] = useState(
    prefetchedEmailTemplates || [],
  );
  const [selectedEmailIdx, setSelectedEmailIdx] = useState(null);
  const [customEmailSubject, setCustomEmailSubject] = useState("");
  const [customEmailBody, setCustomEmailBody] = useState("");
  const [emailSelectionTouched, setEmailSelectionTouched] = useState(false);

  // Fetch stages dynamically if not provided via props
  const [fetchedStages, setFetchedStages] = useState([]);
  const [fetchedPipelineId, setFetchedPipelineId] = useState("");
  const [fetchedOrgId, setFetchedOrgId] = useState("");

  useEffect(() => {
    if (!isOpen || propStages.length > 0) { setFetchedStages([]); return; }
    let cancelled = false;
    const fetchStages = async () => {
      const orgId = propOrgId || user?.organizationId?._id || user?.organizationId || "";
      if (!orgId) return;
      try {
        // Fast path: when pipelineId is known, avoid heavy treatments endpoint.
        let target = null;
        if (propPipelineId) {
          target = { _id: propPipelineId };
        } else {
          const res = await api.get(`/treatments?organizationId=${orgId}`);
          const plans = res?.data || res || [];
          if (cancelled || !plans.length) return;
          target = plans[0];
        }
        if (!target) return;
        setFetchedPipelineId(target._id);
        setFetchedOrgId(orgId);
        const stageCacheKey = `${orgId}:${target._id}`;
        const cachedStages = stageCache.get(stageCacheKey);
        if (cachedStages && Date.now() - cachedStages.ts < WRAPUP_CACHE_TTL_MS) {
          if (!cancelled) setFetchedStages(cachedStages.data);
          return;
        }
        // IMPORTANT: use lightweight stage endpoint; pipeline details endpoint is heavy.
        const stageRes = await api.get(`/stages/${target._id}`);
        const stgs = stageRes?.data || stageRes || [];
        const normalized = stgs.map((s) => ({
          ...s,
          stageName: s.stageName || s.name,
          stageOrder: s.stageOrder || s.order,
        }));
        stageCache.set(stageCacheKey, { ts: Date.now(), data: normalized });
        if (!cancelled) {
          setFetchedStages(normalized);
        }
      } catch { /* ignore */ }
    };
    fetchStages();
    return () => { cancelled = true; };
  }, [isOpen, propStages.length, propPipelineId, propOrgId, user]);

  const stages = propStages.length > 0 ? propStages : fetchedStages;
  const pipelineId = propPipelineId || fetchedPipelineId;
  const organizationId = propOrgId || fetchedOrgId;

  const stageChanged = !currentStageId || selectedStageId !== currentStageId;
  const canSubmit = outcome && (selectedStageId || stages.length === 0) && stageChanged;

  const resolvedPatientId = lead?.patientId || lead?._id;
  const leadPhone = lead?.phone || lead?.contact?.phone;
  const leadEmail = lead?.contact?.email || lead?.email;

  const selectedFolder = smsFolders.find((f) => f.folderId === selectedSmsFolderId);
  const folderTemplates = selectedFolder?.templates || [];
  const hasSmsTemplates = smsFolders.some((f) => f.templates?.length > 0);
  const hasSmsSelected = !!(selectedSmsTemplateId && smsBody.trim());
  const hasTemplateEmailSelected =
    selectedEmailIdx !== null && emailTemplates[selectedEmailIdx];
  const hasCustomEmailSelected =
    !!customEmailSubject.trim() && !!customEmailBody.trim();
  const hasEmailSelected = hasTemplateEmailSelected || hasCustomEmailSelected;

  // Auto-generate notes from outcome + follow-up actions
  const followUpActions = useMemo(() => {
    const actions = [];
    if (hasSmsSelected) actions.push("SMS");
    if (hasEmailSelected) actions.push("Email");
    if (callbackDate) actions.push("Callback Scheduled");
    return actions;
  }, [hasSmsSelected, hasEmailSelected, callbackDate]);

  const autoGeneratedNote = useMemo(
    () => buildAutoNote(outcome, followUpActions),
    [outcome, followUpActions]
  );

  const displayedNotes = customNotes !== null ? customNotes : autoGeneratedNote;

  // Persist wrapup state to localStorage
  const persistState = useCallback(() => {
    if (!isOpen || !lead) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        leadId: resolvedPatientId,
        leadFirstName: lead?.firstName,
        leadLastName: lead?.lastName,
        leadPhone,
        twilioCallSid,
        lastCallStatus,
        currentStageId,
        sessionId,
        pipelineId,
        organizationId,
        locationId,
        isPowerDialer,
        savedAt: Date.now(),
      }));
    } catch {}
  }, [isOpen, lead, resolvedPatientId, leadPhone, twilioCallSid, lastCallStatus, currentStageId, sessionId, pipelineId, organizationId, locationId, isPowerDialer]);

  useEffect(() => {
    if (isOpen) persistState();
  }, [isOpen, persistState]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "You have an unsaved wrap-up. Are you sure you want to leave?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isOpen]);

  // Auto-detect outcome from call status
  useEffect(() => {
    if (!isOpen) return;
    // Initialize once per modal open to avoid late status races overwriting selection.
    const preset = initialOutcome || mapStatusToOutcome(lastCallStatus);
    setOutcome(preset || "");
  }, [isOpen]); // intentionally only on open

  // Auto-select Converted stage when appointment_booked
  useEffect(() => {
    if (outcome === "appointment_booked" && stages.length > 0) {
      const converted = stages.find((s) => {
        const ln = (s.stageName || "").toLowerCase();
        return ln.includes("converted") || ln.includes("completed") || ln.includes("booked");
      });
      if (converted) setSelectedStageId(converted._id);
    }
  }, [outcome, stages]);

  useEffect(() => {
    // Lazy-load providers only when appointment details are needed.
    if (!isOpen || outcome !== "appointment_booked" || providers.length > 0) return;
    recareService.getProviders()
      .then((res) => setProviders(res?.data || []))
      .catch(() => {});
  }, [isOpen, outcome, providers.length]);

  // Reset state when modal opens — pre-select current stage
  useEffect(() => {
    if (isOpen) {
      setCustomNotes(null);
      setSelectedStageId(currentStageId || "");
      setCallbackDate("");
      setBookingDate("");
      setBookingTime("");
      setBookingProviderId("");
      setBookingProviderName("");
      setSmsBody("");
      setSelectedSmsFolderId("");
      setSelectedSmsTemplateId("");
      setSelectedEmailIdx(null);
      setCustomEmailSubject("");
      setCustomEmailBody("");
      setEmailSelectionTouched(false);
    }
  }, [isOpen, currentStageId]);

  useEffect(() => {
    if (!isOpen || !locationId) return;
    // If parent already provided templates, avoid extra API round-trips on modal open.
    if (
      Array.isArray(prefetchedSmsFolders) &&
      Array.isArray(prefetchedEmailTemplates)
    ) {
      setSmsFolders(prefetchedSmsFolders);
      setEmailTemplates(prefetchedEmailTemplates);
      return;
    }
    let cancelled = false;
    const fetchTemplates = async () => {
      try {
        const cached = communicationCache.get(locationId);
        if (cached && Date.now() - cached.ts < WRAPUP_CACHE_TTL_MS) {
          setSmsFolders(cached.smsFolders || []);
          setEmailTemplates(cached.emailTemplates || []);
          return;
        }
        const [smsRes, emailRes] = await Promise.allSettled([
          smsTemplateService.getFoldersByLocation(locationId),
          smsTemplateService.getMissedCallTemplates(locationId),
        ]);
        if (!cancelled) {
          let nextSmsFolders = [];
          let nextEmailTemplates = [];
          if (smsRes.status === "fulfilled") {
            nextSmsFolders = smsRes.value.data || smsRes.value || [];
            setSmsFolders(nextSmsFolders);
          }
          if (emailRes.status === "fulfilled") {
            const templates = (emailRes.value.data || emailRes.value || [])
              .filter((t) => {
                const hasEmailContent = !!(t?.emailSubject || t?.emailBody);
                const type = String(t?.type || "").toLowerCase();
                // Strict type match to avoid picking voicemail (contains "mail" in its text)
                const looksLikeEmailType = type === "email";
                const isEnabled = t?.enabled !== false;
                return isEnabled && (looksLikeEmailType || hasEmailContent);
              });
            nextEmailTemplates = templates;
            setEmailTemplates(nextEmailTemplates);
          }
          communicationCache.set(locationId, {
            ts: Date.now(),
            smsFolders: nextSmsFolders,
            emailTemplates: nextEmailTemplates,
          });
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
      }
    };
    fetchTemplates();
    return () => { cancelled = true; };
  }, [
    isOpen,
    locationId,
    prefetchedSmsFolders,
    prefetchedEmailTemplates,
  ]);

  // Auto-select default SMS template from localStorage
  useEffect(() => {
    if (smsFolders.length === 0) return;
    const folderId = localStorage.getItem("lqDefaultSmsFolderId");
    const templateId = localStorage.getItem("lqDefaultSmsTemplateId");
    if (folderId && templateId) {
      const folder = smsFolders.find((f) => f.folderId === folderId);
      if (folder?.templates?.some((t) => t._id === templateId)) {
        setSelectedSmsFolderId(folderId);
        setSelectedSmsTemplateId(templateId);
      }
    }
  }, [smsFolders]);

  // Auto-select default Email template from localStorage
  useEffect(() => {
    if (!isOpen) return;
    if (!leadEmail) return;
    if (emailSelectionTouched) return;
    if (selectedEmailIdx !== null) return; // don't override user choice
    if (!emailTemplates || emailTemplates.length === 0) return;
    const defaultEmailId = localStorage.getItem("lqDefaultEmailTemplateId");
    if (!defaultEmailId) return;
    const idx = emailTemplates.findIndex((t) => String(t?._id) === String(defaultEmailId));
    if (idx >= 0) setSelectedEmailIdx(idx);
  }, [isOpen, leadEmail, emailTemplates, selectedEmailIdx, emailSelectionTouched]);

  const agentFirstName = user?.firstName || "";
  const agentLastName = user?.lastName || "";

  const replaceTemplateVars = useCallback((text) => {
    if (!text) return text;
    return text
      // Agent/sender variables — "This is {{firstName}}" means the agent
      .replace(/\{\{firstName\}\}/g, agentFirstName)
      .replace(/\{\{lastName\}\}/g, agentLastName)
      .replace(/\{\{fullName\}\}/g, agentFullName)
      .replace(/\{\{agentName\}\}/g, agentFullName)
      .replace(/\{\{agentFirstName\}\}/g, agentFirstName)
      .replace(/\{\{agentLastName\}\}/g, agentLastName)
      // Patient/lead variables
      .replace(/\{\{patientFirstName\}\}/g, lead?.firstName || "")
      .replace(/\{\{patientLastName\}\}/g, lead?.lastName || "")
      .replace(/\{\{patientName\}\}/g, `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim())
      .replace(/\{\{patientPhone\}\}/g, leadPhone || "")
      .replace(/\{\{patientEmail\}\}/g, leadEmail || "")
      // General variables
      .replace(/\{\{phone\}\}/g, leadPhone || "")
      .replace(/\{\{email\}\}/g, leadEmail || "")
      .replace(/\{\{companyName\}\}/g, "Our Team")
      .replace(/\{\{date\}\}/g, new Date().toLocaleDateString())
      .replace(/\{\{time\}\}/g, new Date().toLocaleTimeString());
  }, [lead, leadPhone, leadEmail, agentFullName, agentFirstName, agentLastName]);

  // Populate SMS body from selected template
  useEffect(() => {
    if (!selectedSmsTemplateId || !selectedSmsFolderId) {
      setSmsBody("");
      return;
    }
    const folder = smsFolders.find((f) => f.folderId === selectedSmsFolderId);
    const template = folder?.templates?.find((t) => t._id === selectedSmsTemplateId);
    if (template) {
      setSmsBody(replaceTemplateVars(template.body || ""));
    }
  }, [selectedSmsTemplateId, selectedSmsFolderId, smsFolders, replaceTemplateVars]);

  const handleSubmit = async () => {
    if (!outcome) {
      toast.error("Please select a call outcome");
      return;
    }
    if (!selectedStageId && stages.length > 0) {
      toast.error("Please select a stage");
      return;
    }

    setSaving(true);
    try {
      const finalNotes = displayedNotes || "";
      const callPayload = {
        outcome,
        notes: finalNotes,
        followUpActions: [
          ...(hasSmsSelected ? ["send_sms"] : []),
          ...(hasEmailSelected ? ["send_email"] : []),
          ...(callbackDate ? ["schedule_callback"] : []),
        ],
        callbackDate: callbackDate || undefined,
        stageId: selectedStageId || undefined,
        stageName: stages.find((s) => s._id === selectedStageId)?.stageName,
      };

      if (outcome === "appointment_booked" && bookingDate) {
        const scheduledAt = bookingTime
          ? new Date(`${bookingDate}T${bookingTime}`)
          : new Date(bookingDate);
        callPayload.bookingInfo = {
          scheduledAt: scheduledAt.toISOString(),
          providerId: bookingProviderId || undefined,
          providerName: bookingProviderName || providers.find((p) => p.providerId === bookingProviderId)?.providerName || undefined,
        };
      }

      if (twilioCallSid) {
        await callService.updateCallOutcome(twilioCallSid, callPayload);
      }

      if (selectedStageId && selectedStageId !== currentStageId && resolvedPatientId && pipelineId && organizationId) {
        try {
          await api.put(`/pipeline/${organizationId}/pipeline/update-stage`, {
            patientId: resolvedPatientId,
            pipelineId,
            stageId: selectedStageId,
          });
        } catch (err) {
          console.error("Error moving stage:", err);
        }
      }

      if (callbackDate && resolvedPatientId) {
        try {
          await Promise.all([
            api.put(`/patients/${resolvedPatientId}`, {
              nextFollowUpDate: callbackDate,
            }),
            api.post("/callbacks", {
              patientId: resolvedPatientId,
              patientName: `${lead?.firstName || ""} ${lead?.lastName || ""}`.trim(),
              patientPhone: leadPhone,
              scheduledAt: callbackDate,
              notes: finalNotes || undefined,
              locationId,
            }),
          ]);
        } catch (err) {
          console.error("Error scheduling callback:", err);
        }
      }

      // Send SMS if template selected and body is non-empty
      if (hasSmsSelected && leadPhone) {
        try {
          const finalSmsBody = replaceTemplateVars(smsBody.trim());
          await smsService.sendSms({
            to: leadPhone,
            body: finalSmsBody,
            patientId: resolvedPatientId,
            locationId,
          });
          toast.success("SMS sent");
        } catch (err) {
          console.error("Error sending SMS:", err);
          const errMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Unknown error";
          toast.error(`SMS failed: ${errMsg}`, { duration: 5000 });
        }
      }

      // Send Email if template selected
      if (hasEmailSelected) {
        if (leadEmail) {
          try {
            const emailT = hasTemplateEmailSelected
              ? emailTemplates[selectedEmailIdx]
              : null;
            const subject = replaceTemplateVars(
              emailT?.emailSubject || customEmailSubject.trim()
            );
            const body = replaceTemplateVars(
              emailT?.emailBody || customEmailBody.trim()
            );
            if (!subject || !body) {
              throw new Error("Email subject/body cannot be empty");
            }
            await api.post("/sms/send-email", {
              to: leadEmail,
              subject,
              body,
              patientId: resolvedPatientId,
              locationId,
            });
            toast.success("Email sent");
          } catch (err) {
            console.error("Error sending email:", err);
            const errMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Unknown error";
            toast.error(`Email failed: ${errMsg}`, { duration: 5000 });
          }
        }
      }

      try { localStorage.removeItem(STORAGE_KEY); } catch {}

      const followUpMsg = [hasSmsSelected && "SMS", hasEmailSelected && "Email"].filter(Boolean).join(" + ");
      toast.success(`Wrap-up saved${followUpMsg ? ` — Sending ${followUpMsg}` : ""}`);
      onComplete?.({ outcome, notes: finalNotes, callbackDate, stageId: selectedStageId });
    } catch (err) {
      console.error("Error saving wrap-up:", err);
      toast.error("Failed to save wrap-up");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-[2px] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-t-2xl flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold">Call Wrap-Up</h3>
            <p className="text-sm text-white/80">
              {lead?.firstName} {lead?.lastName}
              {leadPhone && <span className="ml-2 font-mono text-xs bg-white/20 px-1.5 py-0.5 rounded">{leadPhone}</span>}
            </p>
          </div>
          {/* No X close button — wrap-up is mandatory */}
        </div>

        {/* Required fields reminder */}
        {!canSubmit && (
          <div className="px-5 pt-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
              <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
              <span>
                {!outcome && !stageChanged
                  ? "Select call outcome and update pipeline stage to continue"
                  : !outcome
                    ? "Select a call outcome to continue"
                    : !stageChanged
                      ? "Update the pipeline stage (must differ from current) to continue"
                      : "Select a pipeline stage to continue"
                }
              </span>
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Call Outcome */}
          <div>
            <label className="block text-xs font-semibold text-surface-700 mb-1.5">
              Call Outcome <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
                style={outcome ? SELECT_STYLE_FILLED : SELECT_STYLE_PLACEHOLDER}
                className={clsx(
                  "w-full appearance-none px-3 pr-8 py-2.5 rounded-xl border text-sm font-medium focus:outline-none focus:ring-2 cursor-pointer",
                  outcome ? "bg-white border-brand-300 focus:ring-brand-500/20" : "bg-surface-50 border-red-300 focus:ring-red-500/20"
                )}
              >
                <option value="" style={OPTION_PLACEHOLDER_STYLE}>-- Select outcome --</option>
                <optgroup label="Contact Made">
                  {CONTACT_OUTCOMES.filter((o) => o.group === "contact").map((o) => (
                    <option key={o.value} value={o.value} style={OPTION_STYLE}>{o.label}</option>
                  ))}
                </optgroup>
                <optgroup label="No Contact">
                  {CONTACT_OUTCOMES.filter((o) => o.group === "no_contact").map((o) => (
                    <option key={o.value} value={o.value} style={OPTION_STYLE}>{o.label}</option>
                  ))}
                </optgroup>
              </select>
              <ChevronDownIcon className="w-4 h-4 text-surface-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Pipeline Stage */}
          {stages.length > 0 && (
            <div>
              <label className="flex items-center justify-between text-xs font-semibold text-surface-700 mb-1.5">
                <span>Move to Stage <span className="text-red-500">*</span></span>
                {currentStageId && (
                  <span className={clsx("text-[10px] font-normal", stageChanged ? "text-green-600" : "text-amber-500")}>
                    {stageChanged ? "Stage will be updated" : "Must change from current stage"}
                  </span>
                )}
              </label>
              <div className="relative">
                <select
                  value={selectedStageId}
                  onChange={(e) => setSelectedStageId(e.target.value)}
                  style={selectedStageId ? SELECT_STYLE_FILLED : SELECT_STYLE_PLACEHOLDER}
                  className={clsx(
                    "w-full appearance-none px-3 pr-8 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 cursor-pointer",
                    !stageChanged ? "bg-amber-50 border-amber-300 focus:ring-amber-500/20" : selectedStageId ? "bg-white border-surface-200 focus:ring-blue-500/20" : "bg-surface-50 border-red-300 focus:ring-red-500/20"
                  )}
                >
                  <option value="" style={OPTION_PLACEHOLDER_STYLE}>-- Select stage --</option>
                  {stages.map((s) => (
                    <option key={s._id} value={s._id} style={OPTION_STYLE}>
                      {s.stageName}{s._id === currentStageId ? " (current)" : ""}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="w-4 h-4 text-surface-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Booking Info */}
          {outcome === "appointment_booked" && (
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 space-y-2">
              <label className="flex items-center gap-2 text-xs font-semibold text-brand-700">
                <CalendarDaysIcon className="w-4 h-4" />
                Appointment Details
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-surface-500 mb-0.5">Date *</label>
                  <input type="date" value={bookingDate} onChange={(e) => setBookingDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} className="w-full px-3 py-2 rounded-lg bg-white border border-brand-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20" style={SELECT_STYLE_FILLED} />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-surface-500 mb-0.5">Time</label>
                  <input type="time" value={bookingTime} onChange={(e) => setBookingTime(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-brand-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20" style={SELECT_STYLE_FILLED} />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-medium text-surface-500 mb-0.5">Doctor / Provider</label>
                {providers.length > 0 ? (
                  <div className="relative">
                    <select value={bookingProviderId} onChange={(e) => { setBookingProviderId(e.target.value); const prov = providers.find((p) => p.providerId === e.target.value); if (prov) setBookingProviderName(prov.providerName); }} style={bookingProviderId ? SELECT_STYLE_FILLED : SELECT_STYLE_PLACEHOLDER} className="w-full appearance-none px-3 pr-8 py-2 rounded-lg bg-white border border-brand-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 cursor-pointer">
                      <option value="" style={OPTION_PLACEHOLDER_STYLE}>Select provider</option>
                      {providers.map((p) => (<option key={p.providerId} value={p.providerId} style={OPTION_STYLE}>{p.providerName}</option>))}
                    </select>
                    <ChevronDownIcon className="w-3.5 h-3.5 text-surface-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                ) : (
                  <input type="text" value={bookingProviderName} onChange={(e) => setBookingProviderName(e.target.value)} placeholder="Enter provider name" className="w-full px-3 py-2 rounded-lg bg-white border border-brand-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20" style={SELECT_STYLE_FILLED} />
                )}
              </div>
            </div>
          )}

          {/* SMS Follow-Up — always shown if templates available */}
          {hasSmsTemplates && (
            <div>
              <label className="flex items-center gap-2 text-xs font-semibold text-surface-700 mb-1.5">
                <ChatBubbleLeftIcon className="w-3.5 h-3.5 text-green-500" />
                Send SMS Follow-Up
                {!leadPhone && <span className="text-[10px] text-red-500 font-normal ml-1">— No phone</span>}
                {hasSmsSelected && leadPhone && <span className="ml-auto text-[10px] text-green-600 font-bold">WILL SEND</span>}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <select
                    value={selectedSmsFolderId}
                    onChange={(e) => { setSelectedSmsFolderId(e.target.value); setSelectedSmsTemplateId(""); setSmsBody(""); }}
                    disabled={!leadPhone}
                    style={selectedSmsFolderId ? SELECT_STYLE_FILLED : SELECT_STYLE_PLACEHOLDER}
                    className="w-full appearance-none px-3 pr-8 py-2 rounded-lg bg-surface-50 border border-surface-200 text-xs focus:outline-none focus:ring-2 focus:ring-green-500/20 cursor-pointer disabled:opacity-50"
                  >
                    <option value="" style={OPTION_PLACEHOLDER_STYLE}>Select folder</option>
                    {smsFolders.filter((f) => f.templates?.length > 0).map((f) => (
                      <option key={f.folderId} value={f.folderId} style={OPTION_STYLE}>{f.folderName} ({f.templates.length})</option>
                    ))}
                  </select>
                  <ChevronDownIcon className="w-3.5 h-3.5 text-surface-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="relative flex-1">
                  <select
                    value={selectedSmsTemplateId}
                    onChange={(e) => setSelectedSmsTemplateId(e.target.value)}
                    disabled={!selectedSmsFolderId || !leadPhone}
                    style={selectedSmsTemplateId ? SELECT_STYLE_FILLED : SELECT_STYLE_PLACEHOLDER}
                    className="w-full appearance-none px-3 pr-8 py-2 rounded-lg bg-surface-50 border border-surface-200 text-xs focus:outline-none focus:ring-2 focus:ring-green-500/20 cursor-pointer disabled:opacity-50"
                  >
                    <option value="" style={OPTION_PLACEHOLDER_STYLE}>Select template</option>
                    {folderTemplates.map((t) => (
                      <option key={t._id} value={t._id} style={OPTION_STYLE}>{t.title}</option>
                    ))}
                  </select>
                  <ChevronDownIcon className="w-3.5 h-3.5 text-surface-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
              {smsBody && (
                <textarea
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  placeholder="Edit SMS message..."
                  rows={3}
                  className="mt-2 w-full px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-green-500/20"
                  style={SELECT_STYLE_FILLED}
                />
              )}
            </div>
          )}

          {/* Email Follow-Up — always shown if templates available */}
          {(emailTemplates.length > 0 || leadEmail) && (
            <div>
              <label className="flex items-center gap-2 text-xs font-semibold text-surface-700 mb-1.5">
                <EnvelopeIcon className="w-3.5 h-3.5 text-blue-500" />
                Send Email Follow-Up
                {!leadEmail && <span className="text-[10px] text-red-500 font-normal ml-1">— No email</span>}
                {hasEmailSelected && leadEmail && <span className="ml-auto text-[10px] text-blue-600 font-bold">WILL SEND</span>}
              </label>
              {emailTemplates.length > 0 && (
                <div className="relative">
                  <select
                    value={selectedEmailIdx ?? ""}
                    onChange={(e) => {
                      setEmailSelectionTouched(true);
                      const next = e.target.value ? Number(e.target.value) : null;
                      setSelectedEmailIdx(next);
                      if (next !== null) {
                        setCustomEmailSubject("");
                        setCustomEmailBody("");
                      }
                    }}
                    disabled={!leadEmail}
                    style={(selectedEmailIdx !== null) ? SELECT_STYLE_FILLED : SELECT_STYLE_PLACEHOLDER}
                    className="w-full appearance-none px-3 pr-8 py-2 rounded-lg bg-surface-50 border border-surface-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 cursor-pointer disabled:opacity-50"
                  >
                    <option value="" style={OPTION_PLACEHOLDER_STYLE}>— Use custom email / Don't send —</option>
                    {emailTemplates.map((t, i) => (
                      <option key={i} value={i} style={OPTION_STYLE}>{t.title || `Email Template ${i + 1}`}</option>
                    ))}
                  </select>
                  <ChevronDownIcon className="w-3.5 h-3.5 text-surface-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              )}

              {selectedEmailIdx !== null && emailTemplates[selectedEmailIdx] ? (
                <div className="mt-1.5 px-3 py-2 text-xs bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="font-medium" style={SELECT_STYLE_FILLED}>{emailTemplates[selectedEmailIdx].emailSubject}</p>
                  <p className="text-surface-500 line-clamp-2 mt-0.5">{emailTemplates[selectedEmailIdx].emailBody}</p>
                </div>
              ) : (
                <div className="mt-1.5 space-y-2">
                  <input
                    type="text"
                    value={customEmailSubject}
                    onChange={(e) => setCustomEmailSubject(e.target.value)}
                    disabled={!leadEmail}
                    placeholder="Email subject"
                    className="w-full px-3 py-2 rounded-lg bg-surface-50 border border-surface-200 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50"
                    style={SELECT_STYLE_FILLED}
                  />
                  <textarea
                    value={customEmailBody}
                    onChange={(e) => setCustomEmailBody(e.target.value)}
                    disabled={!leadEmail}
                    rows={3}
                    placeholder="Type email message..."
                    className="w-full px-3 py-2 rounded-lg bg-surface-50 border border-surface-200 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50"
                    style={SELECT_STYLE_FILLED}
                  />
                </div>
              )}
            </div>
          )}

          {/* Schedule Callback */}
          {(outcome === "callback_scheduled" || outcome === "follow_up") && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
              <label className="flex items-center gap-2 text-xs font-semibold text-amber-700">
                <CalendarDaysIcon className="w-4 h-4" />
                Schedule Callback
              </label>
              <input type="datetime-local" value={callbackDate} onChange={(e) => setCallbackDate(e.target.value)} min={new Date().toISOString().slice(0, 16)} className="w-full px-3 py-2 rounded-lg bg-white border border-amber-300 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20" style={SELECT_STYLE_FILLED} />
            </div>
          )}

          {/* Notes — auto-generated from outcome + actions, editable */}
          <div>
            <label className="flex items-center justify-between text-xs font-semibold text-surface-700 mb-1.5">
              <span>Call Notes</span>
              {autoGeneratedNote && customNotes === null && (
                <span className="text-[10px] font-normal text-blue-500">Auto-generated — click to edit</span>
              )}
            </label>
            <textarea
              value={displayedNotes}
              onChange={(e) => setCustomNotes(e.target.value)}
              onFocus={() => { if (customNotes === null && autoGeneratedNote) setCustomNotes(autoGeneratedNote); }}
              placeholder="Add notes about this call..."
              rows={3}
              className="w-full px-3 py-2.5 rounded-xl bg-surface-50 border border-surface-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              style={SELECT_STYLE_FILLED}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-200 bg-surface-50 rounded-b-2xl flex-shrink-0">
          <div className="flex flex-col gap-2">
            {/* {onCallAgain && leadPhone && (
              <button
                onClick={() => onCallAgain?.()}
                className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-green-50 border border-green-200 text-green-700 text-sm font-medium hover:bg-green-100 transition-colors"
              >
                <PhoneIcon className="w-4 h-4" />
                Call Again
              </button>
            )} */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleSubmit}
                disabled={saving || !canSubmit}
                className={clsx(
                  "px-5 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
                  canSubmit ? "bg-blue-500 text-white hover:bg-blue-600" : "bg-surface-300 text-surface-500",
                  "flex-1"
                )}
              >
                {saving
                  ? "Saving..."
                  : !outcome
                    ? "Select Outcome to Continue"
                    : !stageChanged
                      ? "Update Stage to Continue"
                      : (!selectedStageId && stages.length > 0)
                        ? "Select Stage to Continue"
                        : (hasSmsSelected || hasEmailSelected)
                          ? `Save & Send ${[hasSmsSelected && "SMS", hasEmailSelected && "Email"].filter(Boolean).join(" + ")}`
                          : "Save & Continue"
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default WrapUpModal;
